import { Box, Flex, List, SegmentedControl, Skeleton, Text } from '@mantine/core';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { isDefined, isEmpty, isNonNullish, range, sumBy } from 'remeda';
import { type PaidByReminder, useLoadPaidByReminder } from 'shared/hooks/use-load-paid-by-reminder';

import { AnalyticsCard } from '../AnalyticsCard';
import { PaidByReminderBar } from '../charts/PaidByReminderBar';
import { PaidByReminderKpi } from '../charts/PaidByReminderKpi';
import { PaidByReminderLegend } from '../charts/PaidByReminderLegend';
import { toPaidByReminderSegments } from '../charts/paid-by-reminder-segments';

const PERIOD_OPTIONS = [6, 12];

const STILL_OPEN_INVOICES = '/invoices?status=unpaid&paused=false&next_step_type_handler=null';

type PaidByReminderCardProps = {
  periodMonths: number;
  onPeriodMonthsChange: (periodMonths: number) => void;
};

export const PaidByReminderCard = ({
  periodMonths,
  onPeriodMonthsChange,
}: PaidByReminderCardProps) => {
  const { t } = useTranslation();
  const { paidByReminder, isPaidByReminderLoading } = useLoadPaidByReminder({ periodMonths });

  return (
    <AnalyticsCard
      title={t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.TITLE)}
      helpContent={<PaidByReminderHelpContent />}
      actions={
        <SegmentedControl
          size="xs"
          value={String(periodMonths)}
          onChange={value => onPeriodMonthsChange(Number(value))}
          data={PERIOD_OPTIONS.map(months => ({
            value: String(months),
            label: t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.PERIOD, { months }),
          }))}
        />
      }
    >
      {isPaidByReminderLoading && <PaidByReminderCardPlaceholder />}
      {isDefined(paidByReminder) && <PaidByReminderCardBody distribution={paidByReminder} />}
    </AnalyticsCard>
  );
};

const HELP = i18nKeys.ANALYTICS.PAID_BY_REMINDER.HELP;

const HELP_DETAILS = [HELP.COUNTED, HELP.BAR, HELP.PERIOD];

const PaidByReminderHelpContent = () => {
  const { t } = useTranslation();

  return (
    <Box>
      <Text fw="bold">{t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.TITLE)}</Text>
      <Text>{t(HELP.SUMMARY)}</Text>
      <List size="sm" mt="sm" spacing="xs">
        {HELP_DETAILS.map(detail => (
          <List.Item key={detail.LABEL}>
            <Text span inherit fw="bold">
              {t(detail.LABEL)}
            </Text>{' '}
            {t(detail.TEXT)}
          </List.Item>
        ))}
      </List>
    </Box>
  );
};

const PLACEHOLDER_LEGEND_ROWS = 5;

const PaidByReminderCardPlaceholder = () => (
  <Box aria-busy>
    <Skeleton h={30} mt="lg" radius={8} />
    <Flex
      component="ul"
      direction="column"
      gap="sm"
      mt="md"
      style={{ padding: 0, listStyleType: 'none' }}
    >
      {range(0, PLACEHOLDER_LEGEND_ROWS).map(row => (
        <Skeleton key={row} component="li" h={14} radius={4} />
      ))}
    </Flex>
  </Box>
);

type PaidByReminderCardBodyProps = {
  distribution: PaidByReminder;
};

const PaidByReminderCardBody = ({ distribution }: PaidByReminderCardBodyProps) => {
  const { t } = useTranslation();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const segments = toPaidByReminderSegments(distribution, t);
  const hasNothingToShow = isEmpty(segments);
  const paidThroughCollection = distribution.steps.filter(({ stepType }) => isNonNullish(stepType));
  const paidThroughCollectionAmount = sumBy(paidThroughCollection, ({ amount }) => amount);

  return (
    <>
      <Flex align="baseline" gap="md" wrap="wrap">
        <PaidByReminderKpi paidThroughCollectionAmount={paidThroughCollectionAmount} />
      </Flex>
      <Box mt="lg">
        {hasNothingToShow ? (
          <Box h={30} bg="gray.2" style={{ borderRadius: 8 }} />
        ) : (
          <PaidByReminderBar segments={segments} onHoveredKeyChange={setHoveredKey} />
        )}
      </Box>
      {hasNothingToShow ? (
        <Text mt="md" size="xs" c="gray.6">
          {t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.EMPTY)}
        </Text>
      ) : (
        <Box mt="md">
          <PaidByReminderLegend
            segments={segments}
            hoveredKey={hoveredKey}
            stillOpenTo={STILL_OPEN_INVOICES}
          />
        </Box>
      )}
    </>
  );
};
